import {
  Box,
  Button,
  Container,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIconUrl from "../../../assets/Overlay.svg";
import {
  getCheckoutLink,
  getStripeSessionDetails,
} from "../../../services/apiV2";
import { axiosErrorMatches } from "../../../helpers";
import { selectGrowthYealyPlan } from "../../../features/pricingPlans/pricingPlans.selector";
import { useAppSelector } from "../../../store/hooks";
import { useState, useEffect } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { selectCurrentPlan } from "../../../features/pricingPlans/pricingPlans.selector";
import { env } from "../../../constants";
import {
  selectIsLoadingUser,
  selectIsUserLoaded,
  selectPromos,
} from "../../../features/user/user.selector";
import useCheckout from "../../../hooks/useCheckout";

export const AnnualOneTimeOffer = () => {
  const navigate = useNavigate();
  const features = [
    "IP Alerts & Risk Protection",
    "Estimated Sales & Sales Velocity",
    "Ranks & Price History (Keepa Style)",
    "Buy Box & Seller Analysis",
    "Competitor Breakdown & Offers",
  ];
  const growthYearlyPlan = useAppSelector(selectGrowthYealyPlan);
  const currentPlan = useAppSelector(selectCurrentPlan);
  const { growthAnnualOneTimePromo } = useAppSelector(selectPromos);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { checkout } = useCheckout();
  const isUserLoaded = useAppSelector(selectIsUserLoaded);

  const from = searchParams.get("from") || "website";
  const asin = searchParams.get("asin");
  const lookupId = searchParams.get("lookupId");

  const backUrl =
    from === "extension"
      ? asin
        ? `https://www.amazon.com/dp/${asin}`
        : `https://www.amazon.com/`
      : asin
        ? `${env.SERVER_URL}/product-analyzer?asin=${asin}${lookupId ? `&lookupId=${lookupId}` : ""}`
        : `${env.SERVER_URL}/product-analyzer`;

  useEffect(() => {
    if (!growthAnnualOneTimePromo && isUserLoaded) {
      navigate("/");
    }
  }, [growthAnnualOneTimePromo, isUserLoaded]);


  const handleUpgradeClick = async () => {
    if (!growthYearlyPlan) {
      console.error("Growth monthly plan not found");
      return;
    }

    setIsLoading(true);
    const redirectUrl = `/annual-one-time-offer/payment-success?redirect=${backUrl}`;
    try {
      await checkout({ plan: growthYearlyPlan, redirectUrl });
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  };

  return (
    <Box>
      <Stack alignItems="center">
        {/* Main Headline */}
        <Typography
          sx={{
            fontWeight: 700,
            color: "#0E0E0F",
            textAlign: "center",
            fontSize: "30px",
            lineHeight: "36px",
          }}
        >
          Upgrade to Annual & Save Big
        </Typography>

        {/* Sub-headline */}
        <Typography
          sx={{
            fontWeight: 600,
            color: "#111111",
            textAlign: "center",
            fontSize: "28px",
            lineHeight: "36px",
            marginTop: "6px",
            fontStyle: "italic",
          }}
        >
          Your Best Value Plan — Limited One-Time Offer
        </Typography>

        {/* Social Proof */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#000000",
          }}
        >
          <StarIcon sx={{ color: "#000000" }} />
          <Typography
            variant="body1"
            sx={{
              fontWeight: 400,
              color: "#000000",
              fontSize: "16px",
              lineHeight: "36px",
            }}
          >
            Most Popular Among Power Sellers
          </Typography>
        </Box>

        {/* Primary CTA Button */}
        <Button
          variant="contained"
          onClick={handleUpgradeClick}
          sx={{
            backgroundColor: "#63FFAB",
            height: "48px",
            borderRadius: "6px",
            color: "#090F0B",
            fontWeight: 600,
            fontSize: "20px",
            textTransform: "none",
            paddingX: "80px",
            cursor: isLoading ? "wait" : "pointer",
            "&:hover": {
              backgroundColor: "#4caf50",
            },
            marginTop: "10px",
          }}
        >
          Get 2 Months Free + 28% OFF
        </Button>

        {/* Pricing Information */}
        <Typography
          sx={{
            fontWeight: 400,
            color: "#45454C",
            textAlign: "center",
            fontSize: "16px",
            lineHeight: "18.4px",
            marginTop: "7px",
          }}
        >
          $24.99/month &#x21e8; billed yearly ($249.90)
        </Typography>

        {/* Features Section */}
        <Box sx={{ marginTop: "22px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <StarIcon sx={{ color: "#ffc107", fontSize: 28 }} />
            <Typography
              component="h3"
              sx={{
                fontWeight: 600,
                color: "#000000",
                fontSize: "26px",
                fontFamily: "Inter",
              }}
            >
              Includes All PRO Features:
            </Typography>
          </Box>

          <List sx={{ pl: 0 }}>
            {features.map((feature, index) => (
              <ListItem
                key={index}
                sx={{
                  height: "auto",
                  padding: 0,
                  gap: 1,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ListItemIcon sx={{ minWidth: "27px" }}>
                  <img
                    src={CheckIconUrl}
                    width={27}
                    height={27}
                    alt="Check"
                    style={{ display: "block" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={feature}
                  primaryTypographyProps={{
                    sx: {
                      color: "#000000",
                      fontSize: "20px",
                      fontWeight: 400,
                      letterSpacing: 0,
                      verticalAlign: "middle",
                    },
                  }}
                  sx={{ flexGrow: 1 }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Secondary CTA Button */}
        <Button
          variant="contained"
          onClick={handleUpgradeClick}
          sx={{
            width: "539px",
            height: "50px",
            fontWeight: 700,
            fontSize: "22px",
            lineHeight: "22px",
            mt: 3,
            cursor: isLoading ? "wait" : "pointer",
          }}
        >
          Upgrade & Save 28%
        </Button>

        {/* Additional Benefits */}
        <Typography
          variant="body2"
          sx={{
            mt: 2,
            color: "#45454C",
            textAlign: "center",
            fontWeight: 400,
            fontSize: 18,
          }}
        >
          Instant access • Cancel anytime
        </Typography>

        {/* Footer/Trust Indicators */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: 2,
            color: "#000000",
          }}
        >
          <Link
            href={backUrl}
            underline="none"
            sx={{
              color: "#445B67",
              fontSize: 18,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
            &nbsp; Back to{" "}
            {from === "extension" ? "Amazon" : "Product Analyzer"}
          </Link>
          <Typography
            variant="body2"
            sx={{
              color: "#445B67",
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            | Trusted by 50,000+ Amazon Sellers
          </Typography>
        </Box>
      </Stack>
      <Outlet />
    </Box>
  );
};

export default AnnualOneTimeOffer;
